import * as THREE from "three";
import { deactivateCirclePick } from "../../../runtime/circlePickState";
import { safeTraverse } from "../../scene/sceneObjectLookups";

export function getCirclePickHits(intersects) {
  return intersects.filter(
    (hit) =>
      hit &&
      hit.object &&
      hit.object.userData &&
      hit.object.userData.info &&
      Array.isArray(hit.object.userData.info.center) &&
      Array.isArray(hit.object.userData.info.normal)
  );
}

export function createCirclePickHoverState() {
  let lastCircleHoverInfo = null;
  let lastCircleObjs = [];
  let lastCirclePrev = [];

  const restore = () => {
    if (lastCircleObjs && lastCircleObjs.length) {
      for (let i = 0; i < lastCircleObjs.length; i++) {
        const obj = lastCircleObjs[i];
        const prev = lastCirclePrev[i] || {};
        if (obj && obj.material && prev.color !== undefined) {
          obj.material.color.setHex(prev.color);
        }
        if (
          obj &&
          obj.material &&
          prev.linewidth !== undefined &&
          obj.material.linewidth !== undefined
        ) {
          obj.material.linewidth = prev.linewidth;
        }
      }
    }
    lastCircleHoverInfo = null;
    lastCircleObjs = [];
    lastCirclePrev = [];
  };

  const applyHover = ({ canvas, info, scene }) => {
    if (lastCircleHoverInfo === info) return;
    restore();
    const objs = [];
    safeTraverse(scene, (obj) => {
      if (obj && obj.userData && obj.userData.info === info && obj.material) {
        objs.push(obj);
      }
    });
    lastCircleObjs = objs;
    lastCirclePrev = objs.map((o) => ({
      color: o.material.color ? o.material.color.getHex() : undefined,
      linewidth: o.material.linewidth,
      opacity: o.material.opacity,
    }));
    for (const o of objs) {
      if (o.material && o.material.color) o.material.color.setHex(0xff0000);
      if (o.material && o.material.linewidth !== undefined) {
        o.material.linewidth = Math.max(8, (o.material.linewidth || 2) + 4);
      }
      if (o.material && o.material.opacity !== undefined) o.material.opacity = 1.0;
    }
    lastCircleHoverInfo = info;
    canvas.style.cursor = "pointer";
  };

  const clearIfActive = () => {
    if (lastCircleHoverInfo) restore();
  };

  return { applyHover, clearIfActive, restore };
}

export function handleCirclePickClick({ hit, restoreLastCircleHover, sceneObjectsRef }) {
  restoreLastCircleHover();
  let bodyUuid = null;
  let p = hit.object;
  while (p) {
    const candidateUuid = p.userData?.bodyUuid || null;
    if (candidateUuid && sceneObjectsRef.current && sceneObjectsRef.current[candidateUuid]) {
      bodyUuid = candidateUuid;
      break;
    }
    p = p.parent;
  }

  const worldCenter = new THREE.Vector3();
  hit.object.getWorldPosition(worldCenter);
  const worldAxis = new THREE.Vector3(0, 0, 1);
  const objQuat = new THREE.Quaternion();
  hit.object.getWorldQuaternion(objQuat);
  worldAxis.applyQuaternion(objQuat).normalize();

  let localCenterToBody = null;
  let localAxisToBody = null;
  if (bodyUuid) {
    const bodyObj = sceneObjectsRef.current[bodyUuid];
    const chain = [];
    const seen = new Set();
    let cur = bodyObj;
    while (cur) {
      if (seen.has(cur)) break;
      seen.add(cur);
      chain.push(cur);
      cur = cur.parent;
    }
    const bodyWorld = new THREE.Matrix4().identity();
    for (let i = chain.length - 1; i >= 0; i--) {
      const n = chain[i];
      if (n && typeof n.updateMatrix === "function") n.updateMatrix();
      if (n && n.matrix) bodyWorld.multiply(n.matrix);
    }
    const inv = bodyWorld.clone().invert();
    const c = worldCenter.clone().applyMatrix4(inv);
    const normalMatrix = new THREE.Matrix3().getNormalMatrix(inv);
    const a = worldAxis.clone().applyMatrix3(normalMatrix).normalize();
    localCenterToBody = [c.x, c.y, c.z];
    localAxisToBody = [a.x, a.y, a.z];
  }

  window.dispatchEvent(
    new CustomEvent("circle-picked", {
      detail: {
        bodyUuid,
        worldCenter: [worldCenter.x, worldCenter.y, worldCenter.z],
        worldAxis: [worldAxis.x, worldAxis.y, worldAxis.z],
        localCenterToBody,
        localAxisToBody,
        localCenterToJointParent: localCenterToBody,
        localAxisToJointParent: localAxisToBody,
      },
    })
  );
  deactivateCirclePick();
}
